import type {WeighingRow} from './types'

/**
 * Actual quantity per prescribed medicine. Each compounding substance (one vial
 * allocation) covers `allocatedDose` of its medicine; the weighed drug scales it:
 * allocatedDose × actual / expected.
 * In the top-up workflow the single DRAW_DRUG reading mixes drug and top-up solvent
 * for the whole preparation, so no per-medicine actual dose can be derived: those
 * medicines are left out rather than printed with a wrong deviation.
 * `coveredDose` lets the caller reject partially weighed medicines.
 */
export function computeActualDoses(rows: WeighingRow[]) {
  const result = new Map<string, {actualDose: number; coveredDose: number}>()
  const invalid = new Set<string>()

  for (const row of latestDrugReadings(rows)) {
    if (!row.medicineId) continue
    if (row.step === 'DRAWN_DRUG') {
      invalid.add(row.medicineId)
      continue
    }
    if (row.step !== 'DRUG') continue

    const expected = row.expectedWeightGrams
    const actual = row.actualWeightGrams
    if (expected == null || expected <= 0 || actual <= 0 || row.allocatedDose == null) {
      invalid.add(row.medicineId)
      continue
    }

    const current = result.get(row.medicineId) ?? {actualDose: 0, coveredDose: 0}
    result.set(row.medicineId, {
      actualDose: current.actualDose + row.allocatedDose * (actual / expected),
      coveredDose: current.coveredDose + row.allocatedDose,
    })
  }

  for (const id of invalid) result.delete(id)
  return result
}

/**
 * One DRUG reading per vial (medicine + substance index): a vial weighed again, e.g. after the cleanroom
 * was reloaded mid-step, keeps only its latest reading, so the covered dose is not counted twice (CY-137).
 * Other steps are left as they are; a solvent is legitimately weighed several times per vial.
 */
export function latestDrugReadings(rows: readonly WeighingRow[]): WeighingRow[] {
  const latestDrug = new Map<string, WeighingRow>()
  for (const row of rows) {
    if (row.step !== 'DRUG' || !row.medicineId) continue
    const key = `${row.medicineId}:${row.substanceIndex}`
    const previous = latestDrug.get(key)
    if (!previous || (row.recordedAt ?? 0) >= (previous.recordedAt ?? 0)) latestDrug.set(key, row)
  }
  const kept = new Set(latestDrug.values())
  return rows.filter(row => row.step !== 'DRUG' || !row.medicineId || kept.has(row))
}

/** The medicines that were weighed on the scale at all (a DRUG reading exists for them). */
export function getWeighedMedicineIds(rows: readonly WeighingRow[]): Set<string> {
  const ids = new Set<string>()
  for (const row of rows) if (row.step === 'DRUG' && row.medicineId) ids.add(row.medicineId)
  return ids
}
